import React from 'react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react';
import MobileAttachments, { MobileAttachmentsState } from './MobileAttachments';
import {
	IDragDroppedFile,
	IEmailStoredFilingInfo,
	IInitializationContext,
	IOutlookAppCommands,
	IOutlookItemInfo,
	OutlookMessageMode,
} from '../state/CorporateArchiveStates/LegalEntityDocumentsStateOutlook';
import API, { IEmailFilingRequest, IEntityTypeUserInfo, IFilingInfoResult, IUserConfigPackage } from '../API';
import { PopupWindow } from '../state/PopupWindow';
import { OutlookDragDropManager } from './OutlookDragDropManager';

export interface IOutlookCtrlProps {
	state: OutlookCtrlState;
}

class OutlookCtrl extends React.Component<IOutlookCtrlProps, {}> {
	render(): React.ReactNode {
		return (
			<>
				<MobileAttachments state={this.props.state.mail} />
			</>
		);
	}
}

export default observer(OutlookCtrl);

export class OutlookCtrlState {
	private userConfig: IUserConfigPackage;
	public mail: MobileAttachmentsState;
	public dragDropManager: OutlookDragDropManager;

	private itemChangedCallbacks: (() => void)[] = [];
	private itemInfoChangedCallbacks: (() => void)[] = [];
	private onMessageCustomPropsUpdatedCallbacks: ((
		info: IEmailStoredFilingInfo,
	) => void)[] = [];

	private itemInfoChangedTimeout?: NodeJS.Timeout;
	private mode: OutlookMessageMode;
	private commands: IOutlookAppCommands;
	
	constructor(userConfig: IUserConfigPackage, mode: OutlookMessageMode) {
		this.userConfig = userConfig;
		this.mode = mode;
		this.dragDropManager = new OutlookDragDropManager();

		makeAutoObservable(this);

		this.commands = {
			getInitializationContext: () => this.getInitializationContext(),
			setSendAndFileLocation: (entityType:IEntityTypeUserInfo | undefined, entityId:string|null, url:string) => this.setSendAndFileLocation(entityType, entityId, url),
			getDragDropManager: () => this.dragDropManager,
			onItemChanged: (callback: () => void) => {
				this.itemChangedCallbacks.push(callback);
			},
			onItemInfoChanged: (callback: () => void) => {
				this.itemInfoChangedCallbacks.push(callback);
			},
			onMessageCustomPropsUpdated: (
				callback: (info: IEmailStoredFilingInfo) => void,
			) => {
				this.onMessageCustomPropsUpdatedCallbacks.push(callback);
			},
			getMessageMode: () => mode,
			getSubject: () => this.getItemSubject(),
			setSubject: (subject: string) => this.setItemSubject(subject),
			getActiveItemInfo: async () => {
				return {
					recipients: await this.getItemRecipients(),
					from: this.getItemSender(),
					subject: await this.getItemSubject(),
					id: Office.context.mailbox.item?.itemId,
					conversationId: Office.context.mailbox.item?.conversationId,
					internetMessageId: Office.context.mailbox.item?.internetMessageId
				};
			},
			getDraftItemId: () => this.getItemId(),
			getAttachments: () => this.getAttachments(),
			getAttachmentContent: (id: string) => this.getAttachmentContent(id),
			insertHTMLLink: (link) => {
				if (Office.context.mailbox.item) {
					Office.context.mailbox.item.body.setSelectedDataAsync(link, {
						coercionType: Office.CoercionType.Html,
					});
				}
			},
			insertFileAttachment: async (name, uri) => {
				if (Office.context.mailbox.item) {

					Office.context.mailbox.item.addFileAttachmentAsync(uri, name);
				}
			},
			useAsEmailTemplate: (to: string[], subject: string, bodyHtml: string) => {
				if (Office.context.mailbox.item) {

					to = to.filter((t) => t && t.trim().length > 0);
					if (to.length > 0) {
						Office.context.mailbox.item.to.setAsync(to, () => { });
					}

					Office.context.mailbox.item.subject.setAsync(subject, { asyncContext: null });
					Office.context.mailbox.item.body.setAsync(bodyHtml, {
						coercionType: Office.CoercionType.Html,
						bodyMode: Office.MailboxEnums.BodyMode.HostConfig
					});
				}
			},
			replyInline: async (to:string, from: string, sent:Date|null, subject: string, bodyHtml: string) => {
				return new Promise<boolean>((resolve) => {
					if (Office.context.mailbox.item) {

						bodyHtml = this.prependReplyHeaders(to, from, sent, subject) + bodyHtml;

						let replySubject = subject;
						if (!subject.toLowerCase().startsWith('re:')) {
							replySubject = 'Re: ' + subject;
						}
				
						let fromAddress = this.extractFromAddress(from);

						//If we're in read mode, use displayNewMessageForm
						if (this.mode === OutlookMessageMode.Read) {
							
							//Reduce bodyHtml to 32k
							if(bodyHtml.length > 32768) {
								bodyHtml = bodyHtml.substring(0, 32768);
							}

							Office.context.mailbox.displayNewMessageForm({
								toRecipients: [fromAddress],
								ccRecipients: [],
								bccRecipients: [],
								subject: replySubject,
								htmlBody: bodyHtml
							});

							resolve(true);
						} else {
							// Show warning dialog that the current draft will be lost
							//if (window.confirm('The current message draft will be lost. Do you want to continue?')) {
								// Prepare reply subject with "Re: " prefix if not already present
							

							// Track completion of all async operations
							const promises: Promise<void>[] = [];

							// Set the "To" field to the original sender
							if (from && from.trim().length > 0) {
								promises.push(new Promise<void>((resolveOp) => {
									Office.context.mailbox.item!.to.setAsync([fromAddress], (result) => {
										if (result.status === Office.AsyncResultStatus.Failed) {
											console.error('Failed to set To field:', result.error);
										}
										resolveOp();
									});
								}));
							}

							// Set the subject line
							promises.push(new Promise<void>((resolveOp) => {
								Office.context.mailbox.item!.subject.setAsync(replySubject, { asyncContext: null }, (result) => {
									if (result.status === Office.AsyncResultStatus.Failed) {
										console.error('Failed to set subject:', result.error);
									}
									resolveOp();
								});
							}));

							// Set the body content
							promises.push(new Promise<void>((resolveOp) => {
								Office.context.mailbox.item!.body.setAsync(bodyHtml, {
									coercionType: Office.CoercionType.Html,
									bodyMode: Office.MailboxEnums.BodyMode.HostConfig
								}, (result) => {
									if (result.status === Office.AsyncResultStatus.Failed) {
										console.error('Failed to set body:', result.error);
									}
									resolveOp();
								});
							}));

							// Wait for all operations to complete
							Promise.all(promises).then(() =>{

								//todo - set the cursor to the start of the message?

								resolve(true);
							});
						}
					} else {
						resolve(false);
					}
				});
			},
			setCategory: (name: string, color: Office.MailboxEnums.CategoryColor) =>
				this.setCategory(name, color),
			refreshFiledNotificationMessage: () => {
				return this.loadMessageFilingInfo(true);
			},
			clearNotificationMessage: () => {
				if (Office.context.mailbox.item) {
					let id = 'filingNotification';
					Office.context.mailbox.item.notificationMessages.removeAsync(id);
				}
			},
			addNotificationMessage: (message, type) => {
				if (Office.context.mailbox.item) {
					let id = 'filingNotification';

					const details: Office.NotificationMessageDetails = {
						type: type,
						message: message,
						icon:
							type ===
								Office.MailboxEnums.ItemNotificationMessageType.ProgressIndicator
								? undefined
								: 'icon1',
						persistent:
							type ===
								Office.MailboxEnums.ItemNotificationMessageType.ProgressIndicator
								? undefined
								: true,
					};

					Office.context.mailbox.item.notificationMessages.replaceAsync(
						id,
						details,
						(result: Office.AsyncResult<void>) => {
							
						},
					);
				}
			},
			getMessageCustomProps: () => this.getMessageCustomProps(),
			setMessageCustomProps: (info) => this.setMessageCustomProps(info),
			launchWindow: async (url: string, onClose?: () => void) => {
				//Add an SSO token into local storage for it to pick up
				window.localStorage.setItem('helmSSO', await this.userConfig.getToken());
				console.log("Launching " + url);
				return new Promise<void>((resolve) => {

					//Use a web popup if it's a SharePoint link.
					//Outlook native popups show in an iframe and sharepoint refuses to display in iframes outside of specific domains that don't include outlook.
					let webPopup = url.indexOf('.sharepoint.com/') > 0;
					if (webPopup) {
						PopupWindow.show(url).then(() => {
							onClose?.();
						});
					} else {

						if (!url.startsWith('https://')) {
							url = window.location.origin + url;
						}

						//Width and height here need to be percentage.
						//In new outlook, it's % of outlook.
						//In old outlook, it's % of entire screen.
						Office.context.ui.displayDialogAsync(
							url,
							{ displayInIframe: true, width: 70, height: 80 },
							(result: Office.AsyncResult<Office.Dialog>) => {
								result.value.addEventHandler(Office.EventType.DialogMessageReceived, this.processDialogMessage.bind(this));
								resolve();
							},
						);

					}

				});
			},
		};

		this.mail = new MobileAttachmentsState(userConfig, this.commands);
		this.initialize();
	}

	extractFromAddress(from: string) {
		//If the from address contains angle brackets, gets the bit in between
		const match = from.match(/<(.*?)>/);
		return match ? match[1] : from;
	}

	prependReplyHeaders(to: string, from: string, sent: Date | null, subject: string) {
		return `<br><br>
		<hr>
		<strong>From</strong>: ${escapeHtml(from)}<br>
		<strong>Sent</strong>: ${sent ? sent.toLocaleString() : ''}<br>
		<strong>To</strong>: ${escapeHtml(to)}<br>
		<strong>Subject</strong>: ${escapeHtml(subject)}<br><br>`;
	}
	
	async setItemSubject(subject: string) : Promise<void> {
		return new Promise<void>(resolve => {
			Office.context.mailbox.item?.subject.setAsync(subject, { asyncContext: null }, () => {
				resolve();
			});
		});
	}


	async setSendAndFileLocation(entityType: IEntityTypeUserInfo | undefined, entityId: string | null, url: string): Promise<void> {
		//Set the message properties
		let filingRequest:IEmailFilingRequest = {
			emailId: await this.commands.getDraftItemId(),
			entityType: entityType?.id || '',
			entityId: entityId || '',
			url: url,
			conversationId: '',
			internetMessageId: '',
			createNewEntity: false,
			senderEmail: Office.context.mailbox.userProfile.emailAddress
		};

		if (Office.context.mailbox?.item) {

			//Call the API to get a tag and update the subject line
			let subject = await this.commands.getSubject();
			if(!this.extractTag(subject)) {
				//If there is no tag, generate one. This will only work if email filing is enabled.
				await new API(await this.userConfig.getToken()).createTag(filingRequest).then((newTag) => {
					if(newTag) {
						//Add it to the subject line
						let newSubject = `[${newTag}] ${subject}`;
						this.commands.setSubject(newSubject);
					}
				});
			}


			Office.context.mailbox.item.loadCustomPropertiesAsync((props) => {
				props.value.set('sendAndFileRequest', JSON.stringify(filingRequest));
				props.value.saveAsync((ctx) => {

					//Need to show a notification message to the user
					if(Office.context.mailbox.item)
						Office.context.mailbox.item.notificationMessages.replaceAsync(
							'sendAndFileMessage',
							{
								type: Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage,
								message: 'Message will be filed to the selected location when you click send.',
								icon: 'icon1',
								persistent: true,
							},
							(result) => {
								if (result.status === Office.AsyncResultStatus.Succeeded) {
									// Notification added successfully
								}
							}
						);
				});
			});
		}
	}
	
	//If the subject contains square brackets, returns the bit in the middle.
	extractTag(subject: string) : string {
		const match = subject.match(/\[(.*?)\]/);
		return match ? match[1] : '';
	}

	private initResultCtx: undefined|Promise<IInitializationContext>;
	private getInitializationContext() : Promise<IInitializationContext> {
		if(!this.initResultCtx) {
			
			this.initResultCtx = new Promise<IInitializationContext>((resolve) => {
				if (Office.context.mailbox.item) {
					Office.context.mailbox.item.getInitializationContextAsync((result) => {
						let resultCtx:IInitializationContext;
						
						if (result.status === Office.AsyncResultStatus.Succeeded && result.value) {
							resultCtx = (JSON.parse(result.value) as IInitializationContext);
						} else {
							resultCtx = ({} as IInitializationContext);
						}

						resolve(resultCtx);
					});
				} else {
					resolve({} as IInitializationContext);
				}
			});
		}

		return this.initResultCtx;
	}

	private processDialogMessage(arg: any) {
		const messageFromDialog = JSON.parse(arg.message);
		if (messageFromDialog.method === 'insertFileAttachment') {
			this.commands.insertFileAttachment(messageFromDialog.name, messageFromDialog.uri);
		}
	}


	private async setCategory(
		name: string,
		color: Office.MailboxEnums.CategoryColor,
	): Promise<void> {
		if (Office.context.mailbox.item) {
			await this.tryEnsureMasterCategoryPresent(name, color);
			await this.addCategory(name);
		}
	}

	private addCategory(name: string): Promise<void> {
		return new Promise<void>((resolve) => {
			if (Office.context.mailbox.item) {
				Office.context.mailbox.item.categories.addAsync(
					[name],
					{},
					(asyncResult) => {
						if (asyncResult.status === Office.AsyncResultStatus.Failed) {
							console.log('failed to add category: ' + asyncResult.error);
						} else {
							resolve();
						}
					},
				);
			} else {
				resolve();
			}
		});
	}

	private tryEnsureMasterCategoryPresent(
		name: string,
		color: Office.MailboxEnums.CategoryColor,
	): Promise<void> {
		return new Promise<void>(async (resolve) => {
			//Get the master categories, and if it's present, set it.
			Office.context.mailbox.masterCategories.getAsync(function (asyncResult) {
				if (asyncResult.status === Office.AsyncResultStatus.Failed) {
					// failed to get the master categories, maybe we don't access to them have
					console.log(asyncResult.error);
					resolve();
				} else {
					let existingMasterCategory = asyncResult.value.find(
						(c) => c.displayName === name,
					);
					if (!existingMasterCategory) {
						// It doesn't exist, Try to create it
						let category = Object.keys(Office.MailboxEnums.CategoryColor)[color]; //It is a string e.g. Preset0.
						const masterCategoriesToAdd = [
							{
								displayName: name,
								color: category,
							},
						];

						Office.context.mailbox.masterCategories.addAsync(
							masterCategoriesToAdd,
							function (asyncResult) {
								if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
									resolve();
								} else {
									console.log(
										'masterCategories.addAsync call failed with error: ' +
										asyncResult.error.message,
									);
									resolve();
								}
							},
						);
					} else {
						//It already exists - we're all good
						resolve();
					}
				}
			});
		});
	}

	public setMessageCustomProps(info: IEmailStoredFilingInfo): Promise<void> {
		this.onMessageCustomPropsUpdatedCallbacks.forEach((cb) => cb(info));

		return new Promise<void>(async (resolve) => {
			if (Office.context.mailbox?.item) {
				Office.context.mailbox.item.loadCustomPropertiesAsync((props) => {
					props.value.set('filingData', JSON.stringify(info));
					props.value.saveAsync((ctx) => {
						resolve();
					});
				});
			} else {
				return Promise.resolve(null);
			}
		});
	}

	public getMessageCustomProps(): Promise<IEmailStoredFilingInfo> {
		return new Promise<IEmailStoredFilingInfo>(async (resolve) => {
			if (Office.context.mailbox?.item) {
				Office.context.mailbox.item.loadCustomPropertiesAsync((props) => {
					
					if(props.value) {
						let filingData = JSON.parse(
							props.value.get('filingData') || '{}',
						) as IEmailStoredFilingInfo;
						resolve(filingData);
					} else {
						resolve({} as IEmailStoredFilingInfo);
					}
				});
			} else {
				return Promise.resolve({});
			}
		});
	}

	public initialize() {
		if(this.mode === OutlookMessageMode.Read || this.mode === OutlookMessageMode.Compose) {
			this.initializeUX();
		}
	}

	public static async waitForOfficeLoad(): Promise<void> {
		console.log("Starting process to wait for office.js to load");
		while (true) {

			if (window.hasOwnProperty('Office')) {
				console.log("Office loaded!");
				//Wait another 100ms just in case
				await new Promise((resolve) => setTimeout(resolve, 100));
				return;
			} else {
				console.log("Office not loaded yet");
				await new Promise((resolve) => setTimeout(resolve, 100));
			}
		}
	}

	private initializeUX() {
		if (Office.context.mailbox) {

			if (Office.context.mailbox.item) {
				//Set the filing notification for the current item
				this.loadMessageFilingInfo();

				//Add recipients handler for current item
				Office.context.mailbox.item.addHandlerAsync(
					Office.EventType.RecipientsChanged,
					this.raiseItemInfoChanged.bind(this),
				);
			}

			//Listen to the HTML5 drag/drop events
			document.addEventListener("dragover", (event) => {
				event.preventDefault();
				this.dragDropManager.handleDragOver(event.clientX, event.clientY);
			});

			document.addEventListener("drop", (event) => {
				event.preventDefault();
				if(event.dataTransfer) {
					const files = Array.from(event.dataTransfer.items).map(item => {
						const file = item.getAsFile();
						if(file) {
							return {
								name: file.name,
								fileContent: file
							} as IDragDroppedFile;
						} else {
							return undefined;
						}
					}).filter(f => f !== undefined) as IDragDroppedFile[];

					this.dragDropManager.handleDrop([], files);
				}
			});

			//Listen to the New Outlook drag/drop events
			Office.context.mailbox.addHandlerAsync(Office.EventType.DragAndDropEvent, (event: Office.DragAndDropEventArgs) => {

					// Get the file name and the contents of the items dropped into the task pane.
					if (event.dragAndDropEventData.type === "drop") {

						const eventData = event.dragAndDropEventData.dataTransfer;
						const files = eventData.files as IDragDroppedFile[];
						this.dragDropManager.handleDrop([], files);

					} else if (event.dragAndDropEventData.type === "dragover") {

						// Notify drag drop manager
						this.dragDropManager.handleDragOver((event.dragAndDropEventData as any)['taskPaneX'], (event.dragAndDropEventData as any)['taskPaneY']);

					}
				},
				(asyncResult) => {
				if (asyncResult.status === Office.AsyncResultStatus.Failed) {
					console.error("Failed to add event handler:", asyncResult.error.message);
					return;
				}
			});

			Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, () => {
				
				//Add recipients handler for new item selection
				if (Office.context.mailbox.item) {
					Office.context.mailbox.item.addHandlerAsync(
						Office.EventType.RecipientsChanged,
						this.raiseItemInfoChanged.bind(this),
					);
				}

				this.loadMessageFilingInfo();
				this.itemChangedCallbacks.forEach((cb) => cb());
			});
		}
	}

	async getItemInfos(itemIds: string[]): Promise<IOutlookItemInfo[]> {
		let result: IOutlookItemInfo[] = [];
		for (const id of itemIds) {
			let itemInfo = await this.loadItemInfo(id);
			if (itemInfo) {
				result.push(itemInfo);
			}
		}
		return result;
	}

	private async loadItemInfo(itemId: string): Promise<IOutlookItemInfo | undefined> {
		return new Promise<IOutlookItemInfo | undefined>((resolve) => {
			Office.context.mailbox.loadItemByIdAsync(itemId, async (asyncResult) => {
				if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
					let item = asyncResult.value;

					let info: IOutlookItemInfo = {
						id: itemId,
						subject: await this.getSubjectAsString(item.subject),
						from: await this.getEmailAddressDetails(item.from),
						recipients: await this.getRecipients(item.to || []),
					};
					resolve(info);
				} else {
					resolve(undefined);
				}
			});
		});
	}

	private async getRecipients(recipients: Office.EmailAddressDetails[] | Office.Recipients): Promise<Office.EmailAddressDetails[]> {
		return new Promise<Office.EmailAddressDetails[]>(async (resolve) => {
			if (Array.isArray(recipients)) {
				//In read mode, the to field is an array
				resolve(recipients);
			} else if ((recipients as any).getAsync) {
				//In compose mode, it's an async request
				(recipients as any).getAsync({}, (result: any) => {
					resolve(result.value);
				});
			}
		});
	}

	private async getEmailAddressDetails(address: Office.EmailAddressDetails | Office.From): Promise<Office.EmailAddressDetails> {
		return new Promise<Office.EmailAddressDetails>((resolve) => {
			if (Object.hasOwn(address, 'getAsync')) {
				address = address as Office.From;
				address.getAsync((result) => {
					resolve(result.value);
				});
			} else {
				resolve(address as Office.EmailAddressDetails);
			}
		});
	}

	private getSubjectAsString(subject: string | Office.Subject) {
		return new Promise<string>((resolve) => {
			if (typeof subject === 'string') {
				resolve(subject);
			} else {
				subject.getAsync((result) => {
					resolve(result.value);
				});
			}
		});
	}

	private async loadMessageFilingInfo(force: boolean = false): Promise<void> {
		let existingFilingInfo = await this.commands.getMessageCustomProps();
		if (existingFilingInfo.filingMessage && !force) {
			this.commands.addNotificationMessage(
				existingFilingInfo.filingMessage,
				Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage,
			);
		} else {
			//Retrieve the filing log and store the filing notification message

			let emailInfo = await this.commands.getActiveItemInfo();
			if (emailInfo?.internetMessageId) {
				let api = new API(await this.userConfig.getToken());
				let filingLog = await api.getFilingInfo(emailInfo.internetMessageId);

				if (filingLog.log.length > 0) {
					let filingMessage = this.generateFilingMessage(emailInfo.internetMessageId, filingLog);
					this.commands.addNotificationMessage(
						filingMessage,
						Office.MailboxEnums.ItemNotificationMessageType.InformationalMessage,
					);

					existingFilingInfo.filingMessage = filingMessage;
					existingFilingInfo.entityId = filingLog.log[0].logEntry.entityId;
					existingFilingInfo.entityType = filingLog.log[0].logEntry.entityType;
					existingFilingInfo.filedUrl = filingLog.log[0].logEntry.filedUrl;
					existingFilingInfo.filingUrl = filingLog.log[0].logEntry.filingUrl;
					
					this.commands.setMessageCustomProps(existingFilingInfo);
				} else {
					this.commands.clearNotificationMessage();
				}
			}
		}
	}

	private generateFilingMessage(
		internetMessageId: string,
		filingLog: IFilingInfoResult
	) {
		//Sort the log entries so the newest is first
		filingLog.log = filingLog.log.sort((a, b) => {
			return b.logEntry.createdDate.getTime() - a.logEntry.createdDate.getTime();
		});

		let thisMessageEntries = filingLog.log.filter(
			(l) => l.logEntry.internetMessageId === internetMessageId,
		);
		let emailEntries = thisMessageEntries.filter((l) => !l.logEntry.isAttachment);
		let attachmentEntries = thisMessageEntries.filter(
			(l) => l.logEntry.isAttachment,
		);

		//Set the categories
		if (thisMessageEntries[0]?.category) {
			this.commands.setCategory(
				thisMessageEntries[0].category.name,
				thisMessageEntries[0].category.color,
			);
		}

		if (thisMessageEntries.length === 0) {
			return `Other items from this conversation are filed to ${filingLog.log[0].logEntry.filedLocationDisplayName}`;
		} else if (emailEntries.length > 0) {
			let thisEmail = emailEntries[0];
			let date = thisEmail.logEntry.modifiedDate.toLocaleDateString();
			let filedUserId = thisEmail.logEntry.createdBy;
			let userDisplayName = filingLog.users.find(
				(u) => u.id === filedUserId,
			)?.displayName;
			let location = thisEmail.logEntry.filedLocationDisplayName;

			let by = '';

			if(userDisplayName) {
				by = ` by ${userDisplayName}`;
			}

			if (attachmentEntries.length === 0) {
				return `Message filed ${date}${by} to ${location}`;
			} else {
				//If some of the attachments are filed, show that
				return `Message and attachments filed ${date}${by} to ${location}`;
			}
		} else if (attachmentEntries.length > 0) {
			let logEntryToShow = attachmentEntries[0];
			let date = logEntryToShow.logEntry.modifiedDate.toLocaleDateString();
			let userDisplayName = filingLog.users.find(
				(u) => u.id === logEntryToShow.logEntry.modifiedBy,
			)?.displayName;

			let by = '';
			if(userDisplayName) {
				by = ` by ${userDisplayName}`;
			}

			let location = logEntryToShow.logEntry.filedLocationDisplayName;
			return `Attachment(s) filed ${date}${by} to ${location}`;
		}

		return '';
	}

	private raiseItemInfoChanged() {
		if (this.mode !== OutlookMessageMode.Compose) {
			return;
		}

		if (this.itemInfoChangedTimeout) {
			clearTimeout(this.itemInfoChangedTimeout);
		}

		this.itemInfoChangedTimeout = setTimeout(() => {
			this.itemInfoChangedCallbacks.forEach((cb) => cb());
		}, 350);
	}

	private getItemId(): Promise<string> {

		if (Office.context.mailbox.item?.itemId) {
			return Promise.resolve(Office.context.mailbox.item.itemId);
		} else {
			//If we're in compose mode, it might need to be saved as a draft first to get an ID assigned
			return new Promise((resolve) => {
				Office.context.mailbox.item?.saveAsync({}, (saveResult) => {
					resolve(saveResult.value);
				});
			});
		}
	}

	private async getItemSubject(): Promise<string> {
		return new Promise<string>(async (resolve) => {
			if (Office.context.mailbox.item) {
				let sub = await this.getSubjectAsString(Office.context.mailbox.item.subject);
				resolve(sub);
			} else {
				resolve('');
			}
		});
	}

	private async getAttachmentContent(
		id: string,
	): Promise<Office.AttachmentContent | undefined> {
		return new Promise<Office.AttachmentContent | undefined>(async (resolve) => {
			if (Office.context.mailbox.item) {
				Office.context.mailbox.item.getAttachmentContentAsync(id, (result) => {
					resolve(result.value);
				});
			} else {
				resolve(undefined);
			}
		});
	}

	private async getAttachments(): Promise<Office.AttachmentDetails[]> {
		return new Promise<Office.AttachmentDetails[]>(async (resolve) => {
			if (Office.context.mailbox.item) {
				if (Array.isArray(Office.context.mailbox.item.attachments)) {
					//In read mode, the to field is an array
					resolve(Office.context.mailbox.item.attachments);
				} else {
					Office.context.mailbox.item.getAttachmentsAsync((result) => {
						resolve(
							result.value.map((a) => {
								return {
									attachmentType: a.attachmentType,
									id: a.id,
									contentType: '',
									isInline: a.isInline,
									name: a.name,
									size: a.size,
									contentId: a.contentId,
								};
							}),
						);
					});
				}
			} else {
				resolve([]);
			}
		});
	}

	private getItemSender(): Office.EmailAddressDetails | undefined {
		if (Office.context.mailbox.item) {
			return Office.context.mailbox.item.from;
		}
	}

	private async getItemRecipients(): Promise<Office.EmailAddressDetails[]> {
		return new Promise<Office.EmailAddressDetails[]>(async (resolve) => {
			if (Office.context.mailbox.item) {
				if (Array.isArray(Office.context.mailbox.item.to)) {
					//In read mode, the to field is an array
					resolve(Office.context.mailbox.item.to);
				} else if ((Office.context.mailbox.item.to as any).getAsync) {
					//In compose mode, it's an async request
					(Office.context.mailbox.item.to as any).getAsync({}, (result: any) => {
						resolve(result.value);
					});
				}
			} else {
				resolve([]);
			}
		});
	}
}

function escapeHtml(str: string) {
	return str
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}
